import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { modesService } from "@/api/services/modes.service";
import type { CreateModeRequest, UpdateModeRequest } from "@/types/api.types";

/**
 * Get all modes (admin)
 */
export const useModesAdmin = () => {
  return useQuery({
    queryKey: ["modes", "admin"],
    queryFn: () => modesService.getAllModes(),
    refetchInterval: 60000,
  });
};

/**
 * Get mode by slug
 */
export const useModeBySlug = (slug: string) => {
  return useQuery({
    queryKey: ["mode", slug],
    queryFn: () => modesService.getModeBySlug(slug),
    enabled: !!slug,
  });
};

/**
 * Create mode
 */
export const useCreateMode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateModeRequest) => modesService.createMode(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["modes"] });
    },
  });
};

/**
 * Update mode
 */
export const useUpdateMode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateModeRequest }) =>
      modesService.updateMode(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["modes"] });
    },
  });
};

/**
 * Toggle mode
 */
export const useToggleMode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => modesService.toggleMode(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["modes"] });
    },
  });
};

/**
 * Delete mode
 */
export const useDeleteMode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => modesService.deleteMode(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["modes"] });
    },
  });
};
